(function () {
  'use strict';

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
  }

  class HeroShowcaseSlideshow extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._timer = null;
      this._slides = [];
      this._dots = [];
      this._paused = false;
      // Explicit user pause (via the pause/play control). Distinct from the
      // transient hover/focus `_paused`: once the user pauses, autoplay must
      // NOT silently restart on mouseleave/visibility/motion changes.
      this._userPaused = false;
      this._initialized = false;

      // Bound handlers (stable references for add/removeEventListener).
      this._onMouseEnter = this._pause.bind(this);
      this._onMouseLeave = this._resume.bind(this);
      this._onFocusIn = this._pause.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);
    }

    connectedCallback() {
      // Guard against double initialization (theme editor re-inserts nodes).
      if (this._initialized) return;
      this._initialized = true;

      this._track = this.querySelector('[data-hss-track]');
      this._slides = Array.prototype.slice.call(
        this.querySelectorAll('[data-hss-slide]')
      );

      if (this._slides.length === 0) return;

      this._interval = parseInt(
        this.getAttribute('data-autoplay-interval'),
        10
      );
      if (!this._interval || isNaN(this._interval) || this._interval < 1000) {
        this._interval = 6000;
      }

      this._motionQuery =
        window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY);

      this._setupSlides();

      if (this._slides.length > 1) {
        this._buildControls();
        this._bindEvents();
      }

      this._goTo(0, { announce: false });

      if (this._slides.length > 1 && !prefersReducedMotion()) {
        this._startAutoplay();
      }
    }

    disconnectedCallback() {
      this._stopAutoplay();

      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      this.removeEventListener('keydown', this._onKeyDown);
      this.removeEventListener('pointerdown', this._onPointerDown);
      document.removeEventListener(
        'visibilitychange',
        this._onVisibilityChange
      );

      if (this._motionQuery) {
        if (this._motionQuery.removeEventListener) {
          this._motionQuery.removeEventListener(
            'change',
            this._onMotionChange
          );
        } else if (this._motionQuery.removeListener) {
          this._motionQuery.removeListener(this._onMotionChange);
        }
      }

      this._initialized = false;
    }

    /* ---------- setup ---------- */

    _setupSlides() {
      var self = this;
      this._slides.forEach(function (slide, i) {
        slide.classList.add('hero-showcase__slide--managed');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute(
          'aria-label',
          'Slide ' + (i + 1) + ' of ' + self._slides.length
        );
      });
    }

    _buildControls() {
      var self = this;
      var total = this._slides.length;

      var controls = document.createElement('div');
      controls.className = 'hero-showcase__controls';

      // Prev / Next arrows.
      this._prevBtn = document.createElement('button');
      this._prevBtn.type = 'button';
      this._prevBtn.className =
        'hero-showcase__arrow hero-showcase__arrow--prev';
      this._prevBtn.setAttribute('aria-label', 'Previous slide');
      this._prevBtn.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4l-8 8 8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      this._nextBtn = document.createElement('button');
      this._nextBtn.type = 'button';
      this._nextBtn.className =
        'hero-showcase__arrow hero-showcase__arrow--next';
      this._nextBtn.setAttribute('aria-label', 'Next slide');
      this._nextBtn.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      this._prevBtn.addEventListener('click', function () {
        self._manualGo(self._index - 1);
      });
      this._nextBtn.addEventListener('click', function () {
        self._manualGo(self._index + 1);
      });

      // Persistent play/pause toggle (WCAG 2.2.2 — Pause, Stop, Hide).
      // A real, keyboard-reachable control; hover/focus pausing alone does
      // not satisfy 2.2.2 for touch or keyboard-only users.
      this._playPauseBtn = document.createElement('button');
      this._playPauseBtn.type = 'button';
      this._playPauseBtn.className = 'hero-showcase__playpause';
      this._playPauseBtn.addEventListener('click', function () {
        self._toggleUserPause();
      });

      // Dot indicators (tablist).
      this._dotsWrap = document.createElement('div');
      this._dotsWrap.className = 'hero-showcase__dots';
      this._dotsWrap.setAttribute('role', 'tablist');
      this._dotsWrap.setAttribute('aria-label', 'Slides');

      this._dots = [];
      for (var i = 0; i < total; i++) {
        (function (idx) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'hero-showcase__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
          dot.setAttribute('aria-selected', 'false');
          dot.addEventListener('click', function () {
            self._manualGo(idx);
          });
          self._dotsWrap.appendChild(dot);
          self._dots.push(dot);
        })(i);
      }

      // Polite live region for manual navigation announcements.
      this._liveRegion = document.createElement('div');
      this._liveRegion.className = 'hero-showcase__status visually-hidden';
      this._liveRegion.setAttribute('aria-live', 'polite');
      this._liveRegion.setAttribute('aria-atomic', 'true');

      controls.appendChild(this._prevBtn);
      controls.appendChild(this._nextBtn);
      controls.appendChild(this._dotsWrap);
      controls.appendChild(this._playPauseBtn);
      this.appendChild(controls);
      this.appendChild(this._liveRegion);

      // Initial state mirrors the autoplay decision in connectedCallback:
      // playing unless reduced-motion is requested.
      this._reflectPlayState(!prefersReducedMotion());
    }

    // Swap the toggle's accessible name + icon to reflect play/pause state.
    _reflectPlayState(playing) {
      if (!this._playPauseBtn) return;
      this._playPauseBtn.setAttribute(
        'aria-label',
        playing ? 'Pause slideshow' : 'Play slideshow'
      );
      this._playPauseBtn.classList.toggle('is-paused', !playing);
      this._playPauseBtn.innerHTML = playing
        ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5l11 7-11 7z" fill="currentColor"/></svg>';
    }

    // User-driven pause/resume. Sets a sticky `_userPaused` flag so autoplay
    // does not silently restart on hover-out / tab-return / motion change.
    _toggleUserPause() {
      if (this._timer) {
        // Currently autoplaying → user wants it stopped (and kept stopped).
        this._userPaused = true;
        this._stopAutoplay();
        this._reflectPlayState(false);
      } else {
        // Not autoplaying → user wants it running. Clear the sticky pause
        // and reflect whatever state actually results (a no-op under
        // reduced-motion / single-slide stays "paused").
        this._userPaused = false;
        this._startAutoplay();
        this._reflectPlayState(!!this._timer);
      }
    }

    _bindEvents() {
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
      this.addEventListener('keydown', this._onKeyDown);
      this.addEventListener('pointerdown', this._onPointerDown);
      document.addEventListener(
        'visibilitychange',
        this._onVisibilityChange
      );

      if (this._motionQuery) {
        if (this._motionQuery.addEventListener) {
          this._motionQuery.addEventListener('change', this._onMotionChange);
        } else if (this._motionQuery.addListener) {
          this._motionQuery.addListener(this._onMotionChange);
        }
      }
    }

    /* ---------- navigation ---------- */

    _normalize(index) {
      var n = this._slides.length;
      return ((index % n) + n) % n;
    }

    _goTo(index, opts) {
      opts = opts || {};
      var next = this._normalize(index);
      this._index = next;

      this._slides.forEach(function (slide, i) {
        var active = i === next;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      this._dots.forEach(function (dot, i) {
        var active = i === next;
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          dot.setAttribute('aria-current', 'true');
          dot.tabIndex = 0;
        } else {
          dot.removeAttribute('aria-current');
          dot.tabIndex = -1;
        }
        dot.classList.toggle('is-active', active);
      });

      if (opts.announce && this._liveRegion) {
        this._liveRegion.textContent =
          'Slide ' + (next + 1) + ' of ' + this._slides.length;
      }
    }

    // Manual navigation: announce + restart autoplay timer.
    _manualGo(index) {
      this._goTo(index, { announce: true });
      if (this._timer) {
        this._stopAutoplay();
        if (!this._paused && !prefersReducedMotion()) {
          this._startAutoplay();
        }
      }
    }

    /* ---------- autoplay ---------- */

    _startAutoplay() {
      if (this._slides.length <= 1) return;
      if (prefersReducedMotion()) return;
      // Sticky user pause overrides every autoplay (re)start path
      // (hover-out, tab-return, motion-change, manual nav).
      if (this._userPaused) return;
      this._stopAutoplay();
      var self = this;
      this._timer = window.setInterval(function () {
        self._goTo(self._index + 1, { announce: false });
      }, this._interval);
    }

    _stopAutoplay() {
      if (this._timer) {
        window.clearInterval(this._timer);
        this._timer = null;
      }
    }

    _pause() {
      this._paused = true;
      this._stopAutoplay();
    }

    _resume() {
      this._paused = false;
      if (
        this._slides.length > 1 &&
        !prefersReducedMotion() &&
        document.visibilityState !== 'hidden'
      ) {
        this._startAutoplay();
      }
    }

    /* ---------- handlers ---------- */

    _onFocusOut(event) {
      // Only resume if focus left the element entirely.
      if (!this.contains(event.relatedTarget)) {
        this._resume();
      }
    }

    _onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._manualGo(this._index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._manualGo(this._index + 1);
      }
    }

    _onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this._stopAutoplay();
      } else if (!this._paused) {
        this._resume();
      }
    }

    _onMotionChange(event) {
      if (event.matches) {
        this._stopAutoplay();
      } else if (!this._paused) {
        this._startAutoplay();
      }
    }

    /* ---------- swipe ---------- */

    _onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this._swipeStartX = event.clientX;
      this._swipeStartY = event.clientY;
      this._swiping = true;
      window.addEventListener('pointerup', this._onPointerUp, { once: true });
      window.addEventListener('pointercancel', this._onPointerUp, {
        once: true,
      });
    }

    _onPointerUp(event) {
      if (!this._swiping) return;
      this._swiping = false;
      var dx = event.clientX - this._swipeStartX;
      var dy = event.clientY - this._swipeStartY;
      // Horizontal intent only, with a sensible threshold.
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          this._manualGo(this._index + 1);
        } else {
          this._manualGo(this._index - 1);
        }
      }
    }
  }

  if (!customElements.get('hero-showcase-slideshow')) {
    customElements.define('hero-showcase-slideshow', HeroShowcaseSlideshow);
  }
})();
